<template>
  <div class="image-marker-container">
    <div class="image-wrapper" ref="imageWrapper">
      <img 
        :src="imagePath" 
        class="marker-image" 
        @load="onImageLoad"
        ref="markerImage"
        draggable="false"
        @mousedown="startDrawing"
        @mousemove="handleMouseMove"
        @mouseup="handleMouseUp"
      />
      
      <!-- 标记框 -->
      <div 
        v-for="(marker, index) in markers" 
        :key="index"
        class="marker-box"
        :class="{ active: index === currentIndex }"
        :style="getMarkerStyle(marker)"
        @click="selectMarker(index)"
        @contextmenu.prevent="showContextMenu($event, index)"
      >
        <div class="marker-border"></div>
        <div class="marker-label">{{ marker.type || '未标注' }}</div>
        
        <!-- 删除按钮 -->
        <div v-if="index === currentIndex" class="delete-button" @click.stop="deleteMarkerByIndex(index)">
          <span class="delete-icon">×</span>
        </div>
        
        <!-- 调整大小的控制点 -->
        <div v-if="index === currentIndex" class="resize-handles">
          <div class="resize-handle top-left" @mousedown.stop="startResize($event, index, 'top-left')"></div>
          <div class="resize-handle top-right" @mousedown.stop="startResize($event, index, 'top-right')"></div>
          <div class="resize-handle bottom-left" @mousedown.stop="startResize($event, index, 'bottom-left')"></div>
          <div class="resize-handle bottom-right" @mousedown.stop="startResize($event, index, 'bottom-right')"></div>
          <div class="resize-handle top" @mousedown.stop="startResize($event, index, 'top')"></div>
          <div class="resize-handle bottom" @mousedown.stop="startResize($event, index, 'bottom')"></div>
          <div class="resize-handle left" @mousedown.stop="startResize($event, index, 'left')"></div>
          <div class="resize-handle right" @mousedown.stop="startResize($event, index, 'right')"></div>
        </div>
      </div>
      
      <!-- 右键菜单 -->
      <div 
        v-if="contextMenuVisible"
        class="context-menu"
        :style="contextMenuStyle"
        @click.stop
      >
        <div class="context-menu-item" @click="editMarker">
          <span>编辑</span>
        </div>
        <div class="context-menu-item" @click="deleteMarker">
          <span>删除</span>
        </div>
      </div>
      
      <!-- 绘制中的标记框 -->
      <div 
        v-if="isDrawing"
        class="marker-box drawing"
        ref="drawingBox"
      >
        <div class="marker-border"></div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'ImageMarker',
  props: {
    imagePath: {
      type: String,
      default: ''
    },
    scrollTop: {
      type: Number,
      default: 0
    },
    markers: {
      type: Array,
      default: () => []
    },
    drawingMode: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      imageLoaded: false,
      imageWidth: 0,
      imageHeight: 0,
      containerWidth: 0,
      containerHeight: 0,
      scale: 1,
      isDrawing: false,
      drawingStartX: 0,
      drawingStartY: 0,
      drawingWidth: 0,
      drawingHeight: 0,
      drawAnimationFrame: null,
      currentIndex: -1,
      currentBox: {
        height: 0,
        width: 0,
        x: 0,
        y: 0,
        type: '',
        num: 1,
        content: '',
        picsrc: ''
      },
      contextMenuVisible: false,
      contextMenuStyle: {
        left: '0px',
        top: '0px'
      },
      contextMenuIndex: -1,
      lastDrawTime: 0,
      // 调整大小相关
      isResizing: false,
      resizeHandle: '',
      resizeMarkerIndex: -1,
      resizeStartX: 0,
      resizeStartY: 0,
      resizeStartMarker: null,
      resizeAnimationFrame: null
    }
  },
  mounted() {
    this.initImageMarker();
    window.addEventListener('resize', this.handleResize);
    // 点击其他地方隐藏右键菜单
    document.addEventListener('click', this.hideContextMenu);
    // 添加全局鼠标事件监听，确保绘制过程不会因为鼠标离开图片区域而中断
    document.addEventListener('mousemove', this.handleMouseMove);
    document.addEventListener('mouseup', this.handleMouseUp);
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.handleResize);
    document.removeEventListener('click', this.hideContextMenu);
    // 移除全局鼠标事件监听
    document.removeEventListener('mousemove', this.handleMouseMove);
    document.removeEventListener('mouseup', this.handleMouseUp);
  },
  watch: {
    scrollTop() {
      // 滚动时更新位置
      this.updateMarkerPositions();
    }
  },
  methods: {
    initImageMarker() {
      // 等待图片加载完成
      if (this.$refs.markerImage && this.$refs.markerImage.complete) {
        this.onImageLoad();
      }
    },
    
    onImageLoad() {
      if (!this.$refs.markerImage || !this.$refs.imageWrapper) return;
      
      // 获取图片和容器的实际尺寸
      const image = this.$refs.markerImage;
      const container = this.$refs.imageWrapper;
      
      this.imageWidth = image.naturalWidth;
      this.imageHeight = image.naturalHeight;
      this.containerWidth = container.clientWidth;
      this.containerHeight = container.clientHeight;
      
      // 计算缩放比例
      this.scale = Math.min(
        this.containerWidth / this.imageWidth,
        this.containerHeight / this.imageHeight,
        1
      );
      
      this.imageLoaded = true;
      this.updateMarkerPositions();
    },
    
    handleResize() {
      this.onImageLoad();
    },
    
    updateMarkerPositions() {
      // 滚动时更新标记框位置
      this.$forceUpdate();
    },
    
    getMarkerStyle(marker) {
      if (!this.imageLoaded) return {};
      
      // 获取图片容器的实际尺寸
      const rect = this.$refs.imageWrapper.getBoundingClientRect();
      
      // 将百分比坐标转换为像素坐标
      const left = (marker.x / 100) * rect.width;
      const top = (marker.y / 100) * rect.height;
      const width = (marker.width / 100) * rect.width;
      const height = (marker.height / 100) * rect.height;
      
      return {
        left: `${left}px`,
        top: `${top}px`,
        width: `${width}px`,
        height: `${height}px`
      };
    },
    
    selectMarker(index) {
      this.currentIndex = index;
      this.currentBox = { ...this.markers[index] };
      
      // 触发事件
      this.$emit('update:currentindex', index);
      this.$emit('update:currentbox', this.currentBox);
      // 添加标记框点击事件
      this.$emit('marker-clicked', index);
    },
    
    // 显示右键菜单
    showContextMenu(event, index) {
      this.contextMenuIndex = index;
      this.contextMenuVisible = true;
      this.contextMenuStyle = {
        left: `${event.clientX}px`,
        top: `${event.clientY}px`
      };
    },
    
    // 隐藏右键菜单
    hideContextMenu() {
      this.contextMenuVisible = false;
    },
    
    // 编辑标记
    editMarker() {
      this.hideContextMenu();
      this.selectMarker(this.contextMenuIndex);
      this.$emit('edit-marker', this.markers[this.contextMenuIndex], this.contextMenuIndex);
    },
    
    // 删除标记
    deleteMarker() {
      this.hideContextMenu();
      this.$emit('delete-marker', this.markers[this.contextMenuIndex], this.contextMenuIndex);
    },
    
    // 通过索引删除标记
    deleteMarkerByIndex(index) {
      this.$emit('delete-marker', this.markers[index], index);
    },
    
    // 统一处理鼠标移动事件
    handleMouseMove(e) {
      if (this.isDrawing) {
        this.draw(e);
      } else if (this.isResizing) {
        this.resize(e);
      }
    },
    
    // 统一处理鼠标释放事件
    handleMouseUp(e) {
      if (this.isDrawing) {
        this.endDrawing(e);
      } else if (this.isResizing) {
        this.endResize(e);
      }
    },
    
    // 开始调整标记框大小
    startResize(e, index, handle) {
      if (!this.imageLoaded) return;
      
      // 阻止默认行为和事件冒泡
      e.preventDefault();
      e.stopPropagation();
      
      // 设置调整大小状态
      this.isResizing = true;
      this.resizeHandle = handle;
      this.resizeMarkerIndex = index;
      this.resizeStartX = e.clientX;
      this.resizeStartY = e.clientY;
      
      // 保存标记框的初始状态
      this.resizeStartMarker = { ...this.markers[index] };
      
      // 设置鼠标样式
      document.body.style.cursor = this.getCursorForHandle(handle);
    },
    
    // 调整标记框大小过程
    resize(e) {
      if (!this.isResizing || this.resizeMarkerIndex === -1) return;
      
      // 阻止默认行为
      e.preventDefault();
      
      // 取消之前的动画帧请求，避免堆积
      if (this.resizeAnimationFrame) {
        cancelAnimationFrame(this.resizeAnimationFrame);
      }
      
      // 使用requestAnimationFrame确保平滑调整
      this.resizeAnimationFrame = requestAnimationFrame(() => {
        const deltaX = e.clientX - this.resizeStartX;
        const deltaY = e.clientY - this.resizeStartY;
        
        // 获取图片容器的实际尺寸
        const rect = this.$refs.imageWrapper.getBoundingClientRect();
        
        // 将百分比坐标转换为像素坐标
        const startX = (this.resizeStartMarker.x / 100) * rect.width;
        const startY = (this.resizeStartMarker.y / 100) * rect.height;
        const startWidth = (this.resizeStartMarker.width / 100) * rect.width;
        const startHeight = (this.resizeStartMarker.height / 100) * rect.height;
        
        let newX = startX;
        let newY = startY;
        let newWidth = startWidth;
        let newHeight = startHeight;
        
        // 根据不同的调整手柄计算新的位置和大小
        switch (this.resizeHandle) {
          case 'top-left':
            newX = startX + deltaX;
            newY = startY + deltaY;
            newWidth = startWidth - deltaX;
            newHeight = startHeight - deltaY;
            break;
          case 'top-right':
            newY = startY + deltaY;
            newWidth = startWidth + deltaX;
            newHeight = startHeight - deltaY;
            break;
          case 'bottom-left':
            newX = startX + deltaX;
            newWidth = startWidth - deltaX;
            newHeight = startHeight + deltaY;
            break;
          case 'bottom-right':
            newWidth = startWidth + deltaX;
            newHeight = startHeight + deltaY;
            break;
          case 'top':
            newY = startY + deltaY;
            newHeight = startHeight - deltaY;
            break;
          case 'bottom':
            newHeight = startHeight + deltaY;
            break;
          case 'left':
            newX = startX + deltaX;
            newWidth = startWidth - deltaX;
            break;
          case 'right':
            newWidth = startWidth + deltaX;
            break;
        }
        
        // 确保最小尺寸
        if (newWidth < 10) {
          newWidth = 10;
          if (this.resizeHandle.includes('left')) {
            newX = startX + startWidth - 10;
          }
        }
        if (newHeight < 10) {
          newHeight = 10;
          if (this.resizeHandle.includes('top')) {
            newY = startY + startHeight - 10;
          }
        }
        
        // 确保不超出图片边界
        if (newX < 0) {
          newWidth += newX;
          newX = 0;
        }
        if (newY < 0) {
          newHeight += newY;
          newY = 0;
        }
        if (newX + newWidth > rect.width) {
          newWidth = rect.width - newX;
        }
        if (newY + newHeight > rect.height) {
          newHeight = rect.height - newY;
        }
        
        // 将像素坐标转换回百分比坐标
        const updatedMarker = {
          ...this.markers[this.resizeMarkerIndex],
          x: (newX / rect.width) * 100,
          y: (newY / rect.height) * 100,
          width: (newWidth / rect.width) * 100,
          height: (newHeight / rect.height) * 100
        };
        
        // 计算原始图片上的坐标（用于生成裁剪URL）
        const originalX = Math.round((newX / rect.width) * this.imageWidth);
        const originalY = Math.round((newY / rect.height) * this.imageHeight);
        const originalWidth = Math.round((newWidth / rect.width) * this.imageWidth);
        const originalHeight = Math.round((newHeight / rect.height) * this.imageHeight);
        
        // 生成带有OSS裁剪参数的URL
        // 检查URL是否已包含参数
        const hasQueryParams = this.imagePath.includes('?');
        let croppedImageUrl;
        
        if (hasQueryParams) {
          // 如果已有查询参数，在现有参数后添加裁剪参数
          croppedImageUrl = `${this.imagePath},image/crop,x_${originalX},y_${originalY},w_${originalWidth},h_${originalHeight}`;
        } else {
          // 如果没有查询参数，添加裁剪参数
          croppedImageUrl = `${this.imagePath}?x-oss-process=image/crop,x_${originalX},y_${originalY},w_${originalWidth},h_${originalHeight}`;
        }
        
        updatedMarker.picsrc = croppedImageUrl;
        
        // 更新标记数组
        const updatedMarkers = [...this.markers];
        updatedMarkers[this.resizeMarkerIndex] = updatedMarker;
        this.$emit('update:markers', updatedMarkers);
        
        this.resizeAnimationFrame = null;
      });
    },
    
    // 结束调整标记框大小
    endResize(e) {
      if (!this.isResizing) return;
      
      // 阻止默认行为
      e.preventDefault();
      
      // 清理动画帧
      if (this.resizeAnimationFrame) {
        cancelAnimationFrame(this.resizeAnimationFrame);
        this.resizeAnimationFrame = null;
      }
      
      // 重置调整大小状态
      this.isResizing = false;
      this.resizeHandle = '';
      this.resizeMarkerIndex = -1;
      this.resizeStartX = 0;
      this.resizeStartY = 0;
      this.resizeStartMarker = null;
      
      // 恢复鼠标样式
      document.body.style.cursor = '';
    },
    
    // 根据调整手柄获取对应的鼠标样式
    getCursorForHandle(handle) {
      const cursors = {
        'top-left': 'nw-resize',
        'top-right': 'ne-resize',
        'bottom-left': 'sw-resize',
        'bottom-right': 'se-resize',
        'top': 'n-resize',
        'bottom': 's-resize',
        'left': 'w-resize',
        'right': 'e-resize'
      };
      return cursors[handle] || 'default';
    },
    
    // 开始绘制标记框
    startDrawing(e) {
      // 只响应左键点击
      if (e.button !== 0) return;
      
      if (!this.imageLoaded) return;
      
      // 阻止默认行为，防止图片拖拽
      e.preventDefault();
      
      // 重置绘制状态
      this.isDrawing = false;
      this.drawingStartX = 0;
      this.drawingStartY = 0;
      this.drawingWidth = 0;
      this.drawingHeight = 0;
      this.currentX = 0;
      this.currentY = 0;
      
      const rect = this.$refs.imageWrapper.getBoundingClientRect();
      this.isDrawing = true;
      this.drawingStartX = e.clientX - rect.left;
      this.drawingStartY = e.clientY - rect.top;
      this.currentX = e.clientX;
      this.currentY = e.clientY;
      
      // 立即初始化绘制框样式
      const drawingBox = this.$refs.drawingBox;
      if (drawingBox) {
        drawingBox.style.left = `${this.drawingStartX}px`;
        drawingBox.style.top = `${this.drawingStartY}px`;
        drawingBox.style.width = '1px';
        drawingBox.style.height = '1px';
        drawingBox.style.display = 'block';
        drawingBox.style.opacity = '1';
      }
      
      // 重置节流时间戳
      this.lastDrawTime = 0;
    },
    
    // 绘制标记框过程
    draw(e) {
      if (!this.isDrawing) return;
      
      // 阻止默认行为，防止图片拖拽
      e.preventDefault();
      
      // 取消之前的动画帧请求，避免堆积
      if (this.drawAnimationFrame) {
        cancelAnimationFrame(this.drawAnimationFrame);
      }
      
      // 使用requestAnimationFrame确保平滑绘制
      this.drawAnimationFrame = requestAnimationFrame(() => {
        const rect = this.$refs.imageWrapper.getBoundingClientRect();
        const currentX = e.clientX - rect.left;
        const currentY = e.clientY - rect.top;
        
        // 计算宽度和高度
        const width = Math.abs(currentX - this.drawingStartX);
        const height = Math.abs(currentY - this.drawingStartY);
        
        // 计算左上角位置
        const left = Math.min(currentX, this.drawingStartX);
        const top = Math.min(currentY, this.drawingStartY);
        
        // 更新绘制宽度和高度
        this.drawingWidth = width;
        this.drawingHeight = height;
        
        // 直接更新样式，避免不必要的响应式更新
        const drawingBox = this.$refs.drawingBox;
        if (drawingBox) {
          drawingBox.style.left = `${left}px`;
          drawingBox.style.top = `${top}px`;
          drawingBox.style.width = `${width}px`;
          drawingBox.style.height = `${height}px`;
        }
        
        this.drawAnimationFrame = null;
      });
    },
    
    // 结束绘制标记框
    endDrawing(e) {
      if (!this.isDrawing) return;
      
      // 阻止默认行为，防止图片拖拽
      e.preventDefault();
      
      // 清理动画帧
      if (this.drawAnimationFrame) {
        cancelAnimationFrame(this.drawAnimationFrame);
        this.drawAnimationFrame = null;
      }
      
      const rect = this.$refs.imageWrapper.getBoundingClientRect();
      const endX = e.clientX - rect.left;
      const endY = e.clientY - rect.top;
      
      // 计算标记框的尺寸和位置
      const width = Math.abs(endX - this.drawingStartX);
      const height = Math.abs(endY - this.drawingStartY);
      
      // 只有当标记框有一定大小时才创建标记
      if (width > 5 && height > 5) {
        const left = Math.min(endX, this.drawingStartX);
        const top = Math.min(endY, this.drawingStartY);
        
        // 将像素坐标转换为百分比坐标
        // 使用rect.width和rect.height作为分母，因为这是图片在页面上的实际显示尺寸
        const x = (left / rect.width) * 100;
        const y = (top / rect.height) * 100;
        const markerWidth = (width / rect.width) * 100;
        const markerHeight = (height / rect.height) * 100;
        
        // 计算原始图片上的坐标（用于生成裁剪URL）
        const originalX = Math.round((left / rect.width) * this.imageWidth);
        const originalY = Math.round((top / rect.height) * this.imageHeight);
        const originalWidth = Math.round((width / rect.width) * this.imageWidth);
        const originalHeight = Math.round((height / rect.height) * this.imageHeight);
        
        // 生成带有OSS裁剪参数的URL
        // 检查URL是否已包含参数
        const hasQueryParams = this.imagePath.includes('?');
        let croppedImageUrl;
        
        if (hasQueryParams) {
          // 如果已有查询参数，在现有参数后添加裁剪参数
          croppedImageUrl = `${this.imagePath},image/crop,x_${originalX},y_${originalY},w_${originalWidth},h_${originalHeight}`;
        } else {
          // 如果没有查询参数，添加裁剪参数
          croppedImageUrl = `${this.imagePath}?x-oss-process=image/crop,x_${originalX},y_${originalY},w_${originalWidth},h_${originalHeight}`;
        }
        
        // 创建新标记
        const newMarker = {
          height: markerHeight,
          width: markerWidth,
          x: x,
          y: y,
          type: '',
          num: 1,
          content: '',
          picsrc: croppedImageUrl
        };
        
        // 添加到标记数组
        const updatedMarkers = [...this.markers, newMarker];
        this.$emit('update:markers', updatedMarkers);
        
        // 选中新创建的标记
        this.selectMarker(updatedMarkers.length - 1);
        
        // 触发标记创建事件
        this.$emit('marker-created', newMarker);
      }
      
      // 重置绘制状态
      this.isDrawing = false;
      this.drawingStartX = 0;
      this.drawingStartY = 0;
      this.drawingWidth = 0;
      this.drawingHeight = 0;
      
      // 重置绘制框样式
      const drawingBox = this.$refs.drawingBox;
      if (drawingBox) {
        drawingBox.style.left = '0px';
        drawingBox.style.top = '0px';
        drawingBox.style.width = '0px';
        drawingBox.style.height = '0px';
      }
    }
  }
}
</script>

<style scoped>
.image-marker-container {
  width: 100%;
  height: 100%;
  overflow: auto;
  position: relative;
}

.image-wrapper {
  position: relative;
  display: inline-block;
  margin: 0 auto;
  cursor: crosshair;
}

.marker-image {
  max-width: 100%;
  max-height: 100%;
  display: block;
  user-select: none;
  -webkit-user-drag: none;
}

.marker-box {
  position: absolute;
  border: 2px solid #ff5722;
  background-color: rgba(255, 87, 34, 0.1);
  cursor: pointer;
  transition: all 0.2s ease;
}

.marker-box:hover {
  background-color: rgba(255, 87, 34, 0.2);
  border-color: #e64a19;
}

.marker-box.active {
  background-color: rgba(255, 87, 34, 0.3);
  border-color: #d84315;
  z-index: 10;
  box-shadow: 0 0 0 2px rgba(216, 67, 21, 0.3);
  transform: scale(1.01);
}

.marker-box.drawing {
  background-color: rgba(33, 150, 243, 0.2);
  border-color: #2196f3;
  z-index: 20;
  will-change: left, top, width, height;
  transform: translateZ(0);
  box-shadow: 0 0 0 1px rgba(33, 150, 243, 0.5);
  animation: drawingPulse 1.5s infinite;
}

@keyframes drawingPulse {
  0% {
    box-shadow: 0 0 0 1px rgba(33, 150, 243, 0.5);
  }
  50% {
    box-shadow: 0 0 0 2px rgba(33, 150, 243, 0.8);
  }
  100% {
    box-shadow: 0 0 0 1px rgba(33, 150, 243, 0.5);
  }
}

.marker-border {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  border: 1px dashed rgba(255, 255, 255, 0.8);
}

.marker-label {
  position: absolute;
  top: -20px;
  left: 0;
  background-color: #ff5722;
  color: white;
  padding: 2px 6px;
  font-size: 12px;
  border-radius: 2px;
  white-space: nowrap;
  max-width: 100px;
  overflow: hidden;
  text-overflow: ellipsis;
}

.active .marker-label {
  background-color: #d84315;
}

/* 删除按钮样式 */
.delete-button {
  position: absolute;
  top: -10px;
  right: -10px;
  width: 20px;
  height: 20px;
  background-color: #f44336;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  z-index: 30;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
  transition: all 0.2s ease;
}

.delete-button:hover {
  background-color: #d32f2f;
  transform: scale(1.1);
}

.delete-icon {
  color: white;
  font-size: 14px;
  font-weight: bold;
  line-height: 1;
}

.drawing .marker-label {
  background-color: #2196f3;
}

.drawing-info {
  position: absolute;
  bottom: -25px;
  left: 0;
  background-color: rgba(33, 150, 243, 0.9);
  color: white;
  padding: 4px 8px;
  font-size: 12px;
  border-radius: 4px;
  white-space: nowrap;
  font-weight: 500;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
  z-index: 25;
}

.context-menu {
  position: fixed;
  background-color: white;
  border: 1px solid #e4e7ed;
  border-radius: 4px;
  box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1);
  z-index: 1000;
  padding: 5px 0;
  min-width: 100px;
}

.context-menu-item {
  padding: 8px 16px;
  font-size: 14px;
  color: #606266;
  cursor: pointer;
  transition: all 0.2s;
}

.context-menu-item:hover {
  color: #409eff;
}

/* 调整大小的控制点样式 */
.resize-handles {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  pointer-events: none;
}

.resize-handle {
  position: absolute;
  width: 8px;
  height: 8px;
  background-color: #fff;
  border: 1px solid #2196f3;
  border-radius: 50%;
  pointer-events: auto;
  z-index: 25;
}

.resize-handle.top-left {
  top: -4px;
  left: -4px;
  cursor: nw-resize;
}

.resize-handle.top-right {
  top: -4px;
  right: -4px;
  cursor: ne-resize;
}

.resize-handle.bottom-left {
  bottom: -4px;
  left: -4px;
  cursor: sw-resize;
}

.resize-handle.bottom-right {
  bottom: -4px;
  right: -4px;
  cursor: se-resize;
}

.resize-handle.top {
  top: -4px;
  left: 50%;
  transform: translateX(-50%);
  cursor: n-resize;
}

.resize-handle.bottom {
  bottom: -4px;
  left: 50%;
  transform: translateX(-50%);
  cursor: s-resize;
}

.resize-handle.left {
  left: -4px;
  top: 50%;
  transform: translateY(-50%);
  cursor: w-resize;
}

.marker-image-preview {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  pointer-events: none;
  opacity: 0.8;
}

.resize-handle.right {
  right: -4px;
  top: 50%;
  transform: translateY(-50%);
  cursor: e-resize;
}
</style>